import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getInventory, getInventoryStats,
  shipInventoryItem,
  addInventoryStock, updateInventoryItemById,
  deleteInventoryItemById, linkQrToInventory
} from '../../services/adminApi';
import { toast } from 'react-toastify';

const STATUS_COLORS = {
  manufactured: 'badge-grey',
  in_stock:     'badge-blue',
  shipped:      'badge-yellow',
  registered:   'badge-green',
  invalidated:  'badge-red',
  returned:     'badge-purple'
};

const ITEM_TYPES = ['card', 'bracelet', 'keychain', 'sticker', 'tag', 'pendant', 'badge', 'other'];

// ── Add Stock modal ────────────────────────────────────────────
function AddStockModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    itemType: 'card', quantity: 1, sku: '', batchNumber: '',
    status: 'in_stock', notes: '',
    warehouse: '', shelf: '', bin: '',
    color: '', material: '', size: '', supplier: '', unitPrice: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await addInventoryStock({
        itemType:    form.itemType,
        quantity:    Number(form.quantity),
        sku:         form.sku,
        batchNumber: form.batchNumber || undefined,
        status:      form.status,
        notes:       form.notes,
        location:    { warehouse: form.warehouse, shelf: form.shelf, bin: form.bin },
        metadata:    {
          color:     form.color || undefined,
          material:  form.material || undefined,
          size:      form.size || undefined,
          supplier:  form.supplier || undefined,
          unitPrice: form.unitPrice ? Number(form.unitPrice) : undefined
        }
      });
      toast.success(res.data.message);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add stock');
    } finally { setLoading(false); }
  };

  const field = (label, key, type = 'text', extra = {}) => (
    <div>
      <label style={{ display:'block', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.6px', color:'var(--text3)', marginBottom:5 }}>{label}</label>
      <input className="admin-input" type={type} value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} {...extra} />
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>📦 Add Inventory Stock</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ background:'var(--accent-glow)', border:'1px solid var(--accent-dim)', borderRadius:'var(--radius)', padding:'10px 14px', fontSize:12, color:'var(--accent)' }}>
              Add physical stock items to inventory <strong>without generating QR codes</strong>. Each item gets a unique inventory ID (INV-XXXXXXXX). QR codes can be linked later.
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.6px', color:'var(--text3)', marginBottom:5 }}>Product Type *</label>
                <select className="admin-select" style={{ width:'100%' }} value={form.itemType}
                  onChange={e => setForm(f => ({ ...f, itemType: e.target.value }))}>
                  {ITEM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              {field('Quantity (1–5000)', 'quantity', 'number', { min:1, max:5000, required:true })}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {field('SKU / Part Number', 'sku')}
              {field('Batch / Lot Number', 'batchNumber')}
            </div>

            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.6px', color:'var(--text3)', marginBottom:5 }}>Initial Status</label>
              <select className="admin-select" style={{ width:'100%' }} value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="manufactured">Manufactured</option>
                <option value="in_stock">In Stock</option>
              </select>
            </div>

            <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.8px', color:'var(--text3)', marginTop:4 }}>Location</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {field('Warehouse', 'warehouse')}
              {field('Shelf', 'shelf')}
              {field('Bin', 'bin')}
            </div>

            <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.8px', color:'var(--text3)', marginTop:4 }}>Product Details (optional)</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {field('Color', 'color')}
              {field('Material', 'material')}
              {field('Size', 'size')}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {field('Supplier', 'supplier')}
              {field('Unit Price', 'unitPrice', 'number', { step:'0.01', min:0 })}
            </div>

            {field('Notes', 'notes')}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : `Add ${form.quantity} Item${Number(form.quantity) !== 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Ship modal ─────────────────────────────────────────────────
function ShipModal({ itemId, onClose, onSuccess }) {
  const [form, setForm]   = useState({ trackingNumber: '', carrier: '', recipientName: '', recipientEmail: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await shipInventoryItem(itemId, form);
      toast.success(`${itemId} marked as shipped`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to ship');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3>➜ Ship Item</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--accent)' }}>{itemId}</div>
            {[['Tracking Number *', 'trackingNumber', true], ['Carrier *', 'carrier', true],
              ['Recipient Name', 'recipientName', false], ['Recipient Email', 'recipientEmail', false]
            ].map(([label, key, req]) => (
              <div key={key}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.6px', color:'var(--text3)', marginBottom:5 }}>{label}</label>
                <input className="admin-input" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} required={req} />
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : '➜ Mark as Shipped'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit / Link QR modal ───────────────────────────────────────
function EditModal({ item, onClose, onSuccess }) {
  const [form, setForm] = useState({
    status: item.status || 'in_stock',
    warehouse: item.location?.warehouse || '',
    shelf: item.location?.shelf || '',
    bin: item.location?.bin || '',
    sku: item.sku || '',
    notes: item.notes || ''
  });
  const [qrInput, setQrInput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSave = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateInventoryItemById(item.inventoryId, {
        status: form.status,
        location: { warehouse: form.warehouse, shelf: form.shelf, bin: form.bin },
        sku: form.sku,
        notes: form.notes
      });
      toast.success('Inventory updated');
      onSuccess();
      onClose();
    } catch { toast.error('Failed to update'); }
    finally { setLoading(false); }
  };

  const handleLinkQR = async () => {
    if (!qrInput.trim()) return;
    setLoading(true);
    try {
      await linkQrToInventory(item.inventoryId, qrInput.trim().toUpperCase());
      toast.success(`QR code linked to ${item.inventoryId}`);
      onSuccess();
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to link'); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete inventory item ${item.inventoryId}? This cannot be undone.`)) return;
    try {
      await deleteInventoryItemById(item.inventoryId);
      toast.success('Item deleted');
      onSuccess();
      onClose();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <h3>📦 Edit Inventory Item</h3>
          <div style={{ display:'flex', gap:6 }}>
            <span className="badge badge-blue" style={{ fontFamily:'var(--font-mono)', fontSize:11 }}>{item.inventoryId}</span>
            {item.hasQrCode
              ? <span className="badge badge-green">QR Linked</span>
              : <span className="badge badge-grey">No QR</span>}
          </div>
          <button className="btn-icon" style={{ marginLeft:6 }} onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {item.assetId && (
              <div style={{ fontSize:12, color:'var(--text3)' }}>
                Linked QR: <span style={{ fontFamily:'var(--font-mono)', color:'var(--accent)' }}>{item.assetId}</span>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div>
                <label className="form-label">Status</label>
                <select className="admin-select" style={{ width:'100%' }} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="manufactured">Manufactured</option>
                  <option value="in_stock">In Stock</option>
                  <option value="shipped">Shipped</option>
                  <option value="returned">Returned</option>
                </select>
              </div>
              <div>
                <label className="form-label">SKU</label>
                <input className="admin-input" value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} />
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {[['Warehouse', 'warehouse'], ['Shelf', 'shelf'], ['Bin', 'bin']].map(([lbl, key]) => (
                <div key={key}>
                  <label className="form-label">{lbl}</label>
                  <input className="admin-input" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                </div>
              ))}
            </div>

            <div>
              <label className="form-label">Notes</label>
              <textarea className="admin-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>

            {/* Link QR code section — only show for items without QR */}
            {!item.hasQrCode && (
              <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14, marginTop:4 }}>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>Link QR Code</div>
                <p style={{ fontSize:12, color:'var(--text3)', marginBottom:10 }}>
                  Enter an existing asset ID to link a QR code to this inventory item.
                </p>
                <div style={{ display:'flex', gap:8 }}>
                  <input className="admin-input" style={{ flex:1, fontFamily:'var(--font-mono)' }}
                    value={qrInput} onChange={e => setQrInput(e.target.value.toUpperCase())}
                    placeholder="PET-XXXXXXXX" />
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleLinkQR} disabled={loading || !qrInput.trim()}>
                    Link
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer" style={{ justifyContent:'space-between' }}>
            <button type="button" className="btn btn-danger btn-sm" onClick={handleDelete}>🗑 Delete</button>
            <div style={{ display:'flex', gap:8 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? <span className="spinner" /> : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────
export default function AdminInventory() {
  const [items, setItems]       = useState([]);
  const [pagination, setPagination] = useState({});
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter]     = useState('');
  const [qrFilter, setQrFilter]         = useState('');
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [showAdd, setShowAdd]   = useState(false);
  const [shipItem, setShipItem] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {
      page, limit: 30,
      status: statusFilter || undefined,
      itemType: typeFilter || undefined,
      hasQrCode: qrFilter === '' ? undefined : qrFilter,
      search: search || undefined
    };
    Promise.all([
      getInventory(params),
      getInventoryStats()
    ])
      .then(([inv, st]) => {
        setItems(inv.data.items);
        setPagination(inv.data.pagination);
        setStats(st.data.stats);
      })
      .catch(() => toast.error('Failed to load inventory'))
      .finally(() => setLoading(false));
  }, [page, statusFilter, typeFilter, qrFilter, search]);

  useEffect(() => { load(); }, [load]);

  const byStatus = key => stats?.byStatus?.find(s => s._id === key)?.count ?? 0;

  return (
    <AdminLayout title="Inventory">
      {/* Mini stat bar */}
      <div className="stat-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total', val: stats?.total, icon: '◉' },
          { label: 'In Stock', val: byStatus('in_stock'), icon: '✓', color: 'green' },
          { label: 'Shipped', val: byStatus('shipped'), icon: '➜', color: 'yellow' },
          { label: 'Registered', val: byStatus('registered'), icon: '▣', color: 'green' },
          { label: 'Invalidated', val: byStatus('invalidated'), icon: '✕', color: 'red' },
        ].map(s => (
          <div key={s.label} className={`stat-card ${s.color || ''}`} style={{ padding: '14px 18px' }}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ fontSize: 22 }}>
              {s.val ?? <span className="skeleton" style={{ width: 40, height: 22, display: 'inline-block' }} />}
            </div>
          </div>
        ))}
      </div>

      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="admin-input" placeholder="Search INV-ID, Asset ID, or SKU…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="admin-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="manufactured">Manufactured</option>
          <option value="in_stock">In Stock</option>
          <option value="shipped">Shipped</option>
          <option value="registered">Registered</option>
          <option value="invalidated">Invalidated</option>
          <option value="returned">Returned</option>
        </select>
        <select className="admin-select" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          {ITEM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
        <select className="admin-select" value={qrFilter} onChange={e => { setQrFilter(e.target.value); setPage(1); }}>
          <option value="">QR & Non-QR</option>
          <option value="true">QR Linked Only</option>
          <option value="false">No QR Only</option>
        </select>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            + Add Stock
          </button>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
        {pagination.total ?? '—'} items
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Inventory ID</th>
              <th>Type</th>
              <th>SKU</th>
              <th>Status</th>
              <th>QR Code</th>
              <th>Location</th>
              <th>Batch</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>{Array.from({ length: 8 }).map((_, j) => (
                  <td key={j}><div className="skeleton" style={{ height: 14, width: '80%' }} /></td>
                ))}</tr>
              ))
            ) : items.length === 0 ? (
              <tr><td colSpan={8}>
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <p>No inventory items found. Add stock or generate QR codes.</p>
                </div>
              </td></tr>
            ) : items.map(item => (
              <tr key={item._id}>
                <td className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{item.inventoryId || '—'}</td>
                <td style={{ textTransform: 'capitalize', color: 'var(--text2)' }}>{item.itemType}</td>
                <td className="mono" style={{ fontSize: 11 }}>{item.sku || '—'}</td>
                <td>
                  <span className={`badge ${STATUS_COLORS[item.status] || 'badge-grey'}`}>
                    {item.status?.replace('_', ' ')}
                  </span>
                </td>
                <td>
                  {item.hasQrCode ? (
                    <span className="badge badge-green" style={{ fontFamily:'var(--font-mono)', fontSize:10 }}>{item.assetId}</span>
                  ) : (
                    <span className="badge badge-grey">None</span>
                  )}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                  {[item.location?.warehouse, item.location?.shelf, item.location?.bin].filter(Boolean).join(' / ') || '—'}
                </td>
                <td className="mono" style={{ fontSize: 11 }}>{item.batchNumber?.slice(0, 16) || '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => setEditItem(item)}>Edit</button>
                    {(item.status === 'in_stock' || item.status === 'manufactured') && (
                      <button className="btn btn-sm btn-primary" onClick={() => setShipItem(item.assetId || item.inventoryId)}>Ship</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {pagination.pages > 1 && (
          <div className="pagination">
            <span className="page-info">Page {page} of {pagination.pages}</span>
            <div className="page-btns">
              <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <button className="page-btn" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {showAdd && <AddStockModal onClose={() => setShowAdd(false)} onSuccess={load} />}
      {shipItem && <ShipModal itemId={shipItem} onClose={() => setShipItem(null)} onSuccess={load} />}
      {editItem && <EditModal item={editItem} onClose={() => setEditItem(null)} onSuccess={load} />}
    </AdminLayout>
  );
}
